import { JSX } from 'solid-js';

import { Context } from '@bitiotic/bitiotic';

function Link(props: {
    ctx: Context,
    href: string,
    children: JSX.Element,
}): JSX.Element {
    return <a
        class="font-bold text-blue-700 hover:text-blue-400 visited:text-violet-900"
        href={props.href}
    >{props.children}</a>;
}

function Header(props: {
    children: JSX.Element
}): JSX.Element {
    return <div class="text-l font-bold">{props.children}</div>;
}

function Dep(props: {
    children: JSX.Element,
}): JSX.Element {
    return <span class="italic font-mono">{props.children}</span>;
}

export function About(props: {
    ctx: Context,
}): JSX.Element {
    return <div class="mx-8 md:max-w-3xl">
        <div class="text-xl font-bold">About Numbets</div>

        <p class="p-3">
            Numbets is a companion application for North Star Game's
            {' '} <Link ctx={props.ctx} href="https://www.northstargames.com/products/wits-wagers-deluxe-edition">Wits & Wagers</Link>.
            Numbets provides distributed, digital tracking of players, their guesses and their overall scores.
            You will need to provide the players (e.g., friends), the questions/answers (e.g., the Wits & Wagers cards), and
            a means of sharing the questions (e.g., Google Meet).
        </p>
        <p class="p-3">
            For the trivia questions, if you don't have a copy of Wits & Wagers questions handy, you can use
            any trivia questions where the answer is a number that is greater than zero.
        </p>
        <Header>Starting a New Game</Header>
        <p class="p-3">
            The user that starts a new Numbets game can invite other players by sending the game's URL to them.
            This user is the "manager" of the game,
            and is responsible for asking the question, closing the guessing round,
            and entering the answer into the Numbets app.
            Numbets supports up to seven players per game.
        </p>
        <p class="p-3">
            Start a new game by clicking <Link ctx={props.ctx} href="newgame">New Game</Link> above.
            Give the URL of the newly created
            game to everyone who is playing, and wait for them to log into Numbets and join the game.
            When everyone is ready click the <b>Start First Round</b> button.
        </p>
        <Header>Playing a Round</Header>
        <div class="p-3">
            <p class="p-1">
                <b>Guessing</b>: To start the round, the manager asks the question,
                and then everyone enters their guess on their screen.
                Guesses should be positive numbers, greater than zero.
                Guesses may be updated until the guessing phase is closed.
                Once everyone has submitted a guess (a green checkmark in each Player's card), the manager
                should click the "End Guessing Phase" button.
                This advances the round to the Betting Phase.
            </p>
            <p class="p-1">
                <b>Betting</b>: In the betting phase, each player may bet
                on which range they think the right answer is in.
                Each player is granted a fixed amount (usually $200) to bet,
                but they may also bet previous winnings (if any).
                To close the betting phase, the manager
                should submit the correct answer.  Then Numbets
                will determine which users won what and provide a summary.
            </p>
            <p class="p-1">
                If there are more rounds to play, then once everyone is ready to start the
                next round, the manager should
                click the <b>Start Next Round</b> button.  Otherwise if there are no more rounds,
                Numbets should show the results for the game.
            </p>
        </div>
        <Header>Known shortcomings</Header>
        <div class="p-3">
            <ul class="list-disc mx-12">
                <li>No units on answers.  All answers are "unitless" so make sure everyone agrees on
                    the units of the answer (e.g., meters, miles, year, feet, inches, etc).</li>
                <li>No fine-grained dates in answers.  Only years.  You can use decimal points for months if you
                    want?  But its probably easier to skip questions with a year/month/day answer. </li>
                <li>No answers with "B.C." or "A.D." on the year.  Skip questions that may have plausible
                    answers on either side of B.C. and A.D.</li>
                <li>Maximum of seven players per game.</li>
            </ul>
        </div>
        <Header>Credits and Thank Yous</Header>
        <div class="p-3">
            The Numbets app runs on <Dep>Google Firebase</Dep> and is hosted at <Dep>Bluehost</Dep>.

            It is built in Javascript & HTML with <Dep>SolidJS</Dep> and <Dep>Tailwind CSS</Dep>.

            At development time I relied on
            {' '}<Dep>Typescript</Dep>,
            {' '}<Dep>Webpack</Dep>,
            {' '}<Dep>ESLint</Dep>,
            {' '}<Dep>VSCode</Dep>,
            {' '}<Dep>uvu</Dep> and
            {' '}<Dep>Playwright</Dep>
            {' '}among other tools.
        </div>
    </div>;
}